import { format } from "date-fns";
import React from "react";
import Popup from "../../../reusable-components/popup/popup";
import { connect } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ThemeProvider,
  TextField,
  TableSortLabel,
  InputAdornment,
  Collapse,
  IconButton,
  Box,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import { tableTheme } from "../../../mui-theme";
import { ChangelogApi } from "api/changelog";

interface SubAction {
  type: string;
  newValue?: string;
  previousValue?: string;
  roomTypeName?: string;
  ratePlanName?: string;
}

interface HistoryEntry {
  hotelId: number;
  dateMidday: number;
  timestamp: string;
  email: string;
  type: string;
  newValue: string;
  previousValue: string;
  roomTypeName: string;
  ratePlanName: string;
  subActions?: SubAction[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  activeHotel: {
    hotelId: number;
  };
  dayDate: number;
}

type HistoryModalProps = Props & { ref?: React.ForwardedRef<HistoryModal> };

interface State {
  history: HistoryEntry[];
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  sortConfig: {
    key: keyof HistoryEntry | null;
    direction: "asc" | "desc";
  };
}

interface GroupedHistoryEntry {
  id: string;
  timestamp: string;
  email: string;
  type: string;
  count: number;
  entries: HistoryEntry[];
}

// Row component for collapsible functionality
const Row = ({ group }: { group: GroupedHistoryEntry }) => {
  const [open, setOpen] = React.useState(false);
  const formattedTime = format(
    new Date(Number(group.timestamp)),
    "dd MMM yyyy HH:mm",
  );

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ width: 28 }}>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setOpen(!open)}
              >
                {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
            </Box>
            {formattedTime}
          </Box>
        </TableCell>
        <TableCell>{group.email}</TableCell>
        <TableCell>
          {group.type} ({group.count} sub-action{group.count > 1 ? "s" : ""})
        </TableCell>
        <TableCell>{group.entries[0].previousValue}</TableCell>
        <TableCell>{group.entries[0].newValue}</TableCell>
        <TableCell>{group.entries[0].roomTypeName || "-"}</TableCell>
        <TableCell>{group.entries[0].ratePlanName || "-"}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small" aria-label="base rate changes">
                <TableBody>
                  {group.entries.map((entry, index) => (
                    <React.Fragment key={`${entry.timestamp}-${index}`}>
                      {entry.subActions &&
                        entry.subActions.length > 0 &&
                        entry.subActions.map((subAction, subIndex) => (
                          <TableRow
                            key={`${entry.timestamp}-${index}-sub-${subIndex}`}
                            sx={{ backgroundColor: "#f0f8ff" }}
                          >
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell sx={{ paddingLeft: 4 }}>
                              <Box component="span">{subAction.type}</Box>
                            </TableCell>
                            <TableCell>{subAction.previousValue}</TableCell>
                            <TableCell>{subAction.newValue}</TableCell>
                            <TableCell>{subAction.roomTypeName}</TableCell>
                            <TableCell>{subAction.ratePlanName}</TableCell>
                          </TableRow>
                        ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

class HistoryModal extends React.Component<HistoryModalProps, State> {
  state: State = {
    history: [],
    isLoading: false,
    error: null,
    searchQuery: "",
    sortConfig: {
      key: "timestamp",
      direction: "desc",
    },
  };

  private async fetchHistory() {
    try {
      this.setState({ isLoading: true, error: null });

      const res = await ChangelogApi.getHistory({
        hotelId: this.props.activeHotel.hotelId,
        dayDate: this.props.dayDate,
      });

      const cleanedHistory = res.sort(
        (a: HistoryEntry, b: HistoryEntry) =>
          Number(b.timestamp) - Number(a.timestamp),
      );

      this.setState({
        history: cleanedHistory,
        isLoading: false,
      });
    } catch (error) {
      this.setState({
        error: "Failed to load history data",
        isLoading: false,
      });
    }
  }

  componentDidMount() {
    if (this.props.dayDate) {
      this.fetchHistory();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.dayDate !== prevProps.dayDate) {
      this.fetchHistory();
    }
  }

  private handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      searchQuery: event.target.value,
    });
  };

  private handleSort = (key: keyof HistoryEntry) => {
    const direction =
      this.state.sortConfig.key === key &&
      this.state.sortConfig.direction === "asc"
        ? "desc"
        : "asc";

    this.setState({
      sortConfig: {
        key,
        direction,
      },
    });
  };

  private sortCombinedEntries = (
    entries: Array<{
      type: "group" | "single";
      timestamp: string;
      data: GroupedHistoryEntry | HistoryEntry;
    }>,
    sortConfig: State["sortConfig"],
  ) => {
    if (!sortConfig.key || sortConfig.key === "timestamp") {
      // Default sort by timestamp
      return entries.sort((a, b) => {
        const aTime = Number(a.timestamp);
        const bTime = Number(b.timestamp);
        return sortConfig.direction === "asc" ? aTime - bTime : bTime - aTime;
      });
    }

    // Only sort by fields that are string or number
    const validSortKeys: (keyof HistoryEntry)[] = [
      "timestamp",
      "email",
      "type",
      "newValue",
      "previousValue",
      "roomTypeName",
      "ratePlanName",
    ];

    if (!validSortKeys.includes(sortConfig.key)) {
      return entries;
    }

    return entries.sort((a, b) => {
      let aValue = "";
      let bValue = "";

      // Extract the values based on entry type
      if (a.type === "group") {
        const groupA = a.data as GroupedHistoryEntry;
        aValue =
          (sortConfig.key && String(groupA.entries[0][sortConfig.key])) || "";
      } else {
        const entryA = a.data as HistoryEntry;
        aValue = (sortConfig.key && String(entryA[sortConfig.key])) || "";
      }

      if (b.type === "group") {
        const groupB = b.data as GroupedHistoryEntry;
        bValue =
          (sortConfig.key && String(groupB.entries[0][sortConfig.key])) || "";
      } else {
        const entryB = b.data as HistoryEntry;
        bValue = (sortConfig.key && String(entryB[sortConfig.key])) || "";
      }

      // Compare the values
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;

      // If values are equal, sort by timestamp as secondary sort
      const aTime = Number(a.timestamp);
      const bTime = Number(b.timestamp);
      return sortConfig.direction === "asc" ? aTime - bTime : bTime - aTime;
    });
  };

  private getFilteredAndSortedData = () => {
    const { history, searchQuery, sortConfig } = this.state;

    // Apply search filter
    const filteredData = history.filter((item) => {
      const query = searchQuery.toLowerCase();

      // Format timestamp in multiple ways to make search more flexible
      const timestampDate = new Date(Number(item.timestamp));
      const timestampFormats = [
        format(timestampDate, "yyyy-MMM-dd HH:mm:ss").toLowerCase(),
        format(timestampDate, "dd MMM yyyy").toLowerCase(),
        format(timestampDate, "dd MMM").toLowerCase(),
        format(timestampDate, "d MMM").toLowerCase(),
        format(timestampDate, "MMM dd").toLowerCase(),
        format(timestampDate, "MMM d").toLowerCase(),
      ];

      // Check if query matches any of the timestamp formats
      const timestampMatch = timestampFormats.some((format) =>
        format.includes(query),
      );

      // Check if query matches any subaction values
      const subactionMatch = item.subActions?.some(
        (subaction) =>
          (subaction.type?.toLowerCase() || "").includes(query) ||
          (subaction.newValue?.toString() || "")
            .toLowerCase()
            .includes(query) ||
          (subaction.previousValue?.toString() || "")
            .toLowerCase()
            .includes(query),
      );

      return (
        timestampMatch ||
        (item.email?.toLowerCase() || "").includes(query) ||
        (item.type?.toLowerCase() || "").includes(query) ||
        (item.newValue?.toString() || "").toLowerCase().includes(query) ||
        (item.previousValue?.toString() || "").toLowerCase().includes(query) ||
        (item.roomTypeName?.toString() || "").toLowerCase().includes(query) ||
        (item.ratePlanName?.toString() || "").toLowerCase().includes(query) ||
        subactionMatch
      );
    });

    // Apply sorting
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        const aValue = sortConfig.key
          ? ((a[sortConfig.key] as string | number) ?? "")
          : "";
        const bValue = sortConfig.key
          ? ((b[sortConfig.key] as string | number) ?? "")
          : "";

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filteredData;
  };

  private groupDataByEditBaseRate = (data: HistoryEntry[]) => {
    const baseRateGroups: GroupedHistoryEntry[] = [];
    const otherEntries: HistoryEntry[] = [];

    data.forEach((entry) => {
      // Any entry with subActions gets its own group
      if (entry.subActions && entry.subActions.length > 0) {
        baseRateGroups.push({
          id: `${entry.timestamp}-${entry.email}-${entry.type}`,
          timestamp: entry.timestamp,
          email: entry.email,
          type: entry.type,
          count: entry.subActions.length,
          entries: [entry],
        });
      } else {
        otherEntries.push(entry);
      }
    });

    return { baseRateGroups, otherEntries };
  };

  private renderTableContent() {
    const { isLoading, error } = this.state;
    const filteredAndSortedData = this.getFilteredAndSortedData();

    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">
            Loading...
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center" style={{ color: "red" }}>
            {error}
          </TableCell>
        </TableRow>
      );
    }

    if (filteredAndSortedData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">
            No history data available
          </TableCell>
        </TableRow>
      );
    }

    // Group the data but maintain chronological order
    const { baseRateGroups, otherEntries } = this.groupDataByEditBaseRate(
      filteredAndSortedData,
    );

    // Create a combined array of both regular entries and group entries
    // for proper chronological sorting
    const combinedEntries: Array<{
      type: "group" | "single";
      timestamp: string;
      data: GroupedHistoryEntry | HistoryEntry;
    }> = [
      ...baseRateGroups.map((group) => ({
        type: "group" as const,
        timestamp: group.timestamp,
        data: group,
      })),
      ...otherEntries.map((entry) => ({
        type: "single" as const,
        timestamp: entry.timestamp,
        data: entry,
      })),
    ];

    // Sort all entries according to the current sort configuration
    const sortedEntries = this.sortCombinedEntries(
      combinedEntries,
      this.state.sortConfig,
    );

    // Render the sorted entries
    return (
      <>
        {sortedEntries.map((item, index) => {
          if (item.type === "group") {
            const group = item.data as GroupedHistoryEntry;
            return <Row key={`group-${group.id}`} group={group} />;
          } else {
            const entry = item.data as HistoryEntry;
            return (
              <React.Fragment key={`single-${entry.timestamp}-${index}`}>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ width: 28 }}></Box>
                      {format(
                        new Date(Number(entry.timestamp)),
                        "dd MMM yyyy HH:mm",
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{entry.email}</TableCell>
                  <TableCell>{entry.type}</TableCell>
                  <TableCell>{entry.previousValue}</TableCell>
                  <TableCell>{entry.newValue}</TableCell>
                  <TableCell>{entry.roomTypeName}</TableCell>
                  <TableCell>{entry.ratePlanName}</TableCell>
                </TableRow>
                {entry.subActions &&
                  entry.subActions.length > 0 &&
                  entry.subActions.map((subAction, subIndex) => (
                    <TableRow
                      key={`${entry.timestamp}-${index}-sub-${subIndex}`}
                      sx={{ backgroundColor: "#f0f8ff" }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box sx={{ width: 28 }}></Box>
                        </Box>
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell sx={{ paddingLeft: 4 }}>
                        <Box component="span" sx={{ fontStyle: "italic" }}>
                          {subAction.type}
                        </Box>
                      </TableCell>
                      <TableCell>{subAction.previousValue}</TableCell>
                      <TableCell>{subAction.newValue}</TableCell>
                      <TableCell>{entry.roomTypeName}</TableCell>
                      <TableCell>{entry.ratePlanName}</TableCell>
                    </TableRow>
                  ))}
              </React.Fragment>
            );
          }
        })}
      </>
    );
  }

  render() {
    const { sortConfig } = this.state;

    const content = [
      <ThemeProvider theme={tableTheme} key="table-provider">
        <div>
          <TextField
            fullWidth
            size="small"
            placeholder="Search in all fields..."
            value={this.state.searchQuery}
            onChange={this.handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: "calc(90vh - 300px)",
              overflow: "auto",
              "& .MuiTable-root": {
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  padding: "8px",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  "&:nth-of-type(1)": { width: "15%" }, // Timestamp
                  "&:nth-of-type(2)": { width: "20%" }, // Email
                  "&:nth-of-type(3)": { width: "20%" }, // Action
                  "&:nth-of-type(4)": { width: "10%" }, // Previous Value
                  "&:nth-of-type(5)": { width: "10%" }, // New Value
                  "&:nth-of-type(6)": { width: "12.5%" }, // Room Type
                  "&:nth-of-type(7)": { width: "12.5%" }, // Rate Plan
                },
              },
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ width: 28 }}></Box>
                      <TableSortLabel
                        active={sortConfig.key === "timestamp"}
                        direction={sortConfig.direction}
                        onClick={() => this.handleSort("timestamp")}
                      >
                        Timestamp
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "email"}
                      direction={sortConfig.direction}
                      onClick={() => this.handleSort("email")}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "type"}
                      direction={sortConfig.direction}
                      onClick={() => this.handleSort("type")}
                    >
                      Action
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "previousValue"}
                      direction={sortConfig.direction}
                      onClick={() => this.handleSort("previousValue")}
                    >
                      Previous Value
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "newValue"}
                      direction={sortConfig.direction}
                      onClick={() => this.handleSort("newValue")}
                    >
                      New Value
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "roomTypeName"}
                      direction={sortConfig.direction}
                      onClick={() => this.handleSort("roomTypeName")}
                    >
                      Room Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "ratePlanName"}
                      direction={sortConfig.direction}
                      onClick={() => this.handleSort("ratePlanName")}
                    >
                      Rate Plan
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{this.renderTableContent()}</TableBody>
            </Table>
          </TableContainer>
        </div>
      </ThemeProvider>,
    ];

    const title = this.props.dayDate
      ? `History for ${format(new Date(this.props.dayDate), "dd MMM yyyy")}`
      : "History";

    return (
      <Popup
        isPopupOpen={this.props.isOpen}
        closeOnOutsideClick={true}
        title={title}
        content={content}
        action={this.props.onClose}
        actionName="Close"
        maxWidth="lg"
        cancelAction={this.props.onClose}
      />
    );
  }
}

interface AppState {
  hotelData: {
    activeHotel: {
      hotelId: number;
    };
  };
}

const mapStateToProps = (state: AppState) => ({
  activeHotel: state.hotelData.activeHotel,
});

const ConnectedHistoryModal = connect(mapStateToProps)(HistoryModal);

export default React.forwardRef<HistoryModal, Props>((props, ref) => (
  <ConnectedHistoryModal {...props} ref={ref} />
));
